/** Retur penjualan — SATU sumber rumus untuk dua layar: panel detail di
 *  halaman Penjualan (Sales.jsx) dan menu "Ajukan Retur Penjualan" di halaman
 *  Retur (Retur.jsx, brief owner 4 Sep 2026 P18a). Dulu rumus ini hidup di
 *  Sales.jsx saja; kalau disalin ke layar kedua, dua layar bisa menyarankan
 *  angka berbeda untuk nota yang sama — dan tidak ada yang memberi tahu
 *  (jebakan "duplikasi helper melahirkan penyimpangan", CLAUDE.md). */
import { periksaKunciHp, pesanHpTidakSah } from "./noHp";
import { diskonBaris, diskonTerposting } from "./payloadNota";

/** Cermin `routers/retur.py::_bisa_approve_retur` — role superadmin/admin,
 *  role apa pun yang MENGANDUNG "spv", atau izin eksplisit `retur.update`.
 *  Satu sumber untuk Retur.jsx & Sales.jsx: tombol approver (approve, ganti No
 *  HP kredit) tidak boleh tampil lalu berujung 403. "Lengkapi No HP nota"
 *  BUKAN di sini — gerbangnya jabatan (owner 29b), lihat `components/retur/GerbangLengkapiNoHp`. */
export function bisaKelolaRetur(user, can) {
  const role = (user?.role || "").toLowerCase();
  if (role === "admin" || role === "superadmin") return true;
  if (role.includes("spv")) return true;
  return !!(can && can("retur", "update"));
}

/** Vonis TAGIHAN nota untuk retur — berlaku untuk pengembalian UANG maupun
 *  TUKAR UNIT (jawaban Bos no. 33, 19 Okt 2026; sebelumnya hanya tukar unit,
 *  29a 25 Sep). Nota bertempo yang tagihannya belum lunas tidak boleh diretur
 *  dalam bentuk apa pun: uang benar-benar keluar dari kas kepada orang yang
 *  belum membayar, dan kredit tukar unit menahan nilai barang yang belum
 *  dibayar.
 *
 *  Statusnya hanya diketahui SERVER (dokumen piutang Finance), dibaca dari
 *  `sale._retur_tagihan` — dengan `_tukar_unit_tagihan` sebagai nama lama yang
 *  masih dikirim server supaya bundle di tab lama tidak kehilangan gerbangnya.
 *  Nota bertempo TANPA kedua medan itu = status tidak diketahui -> DITAHAN:
 *  pilihan yang mati dengan alasan tertulis lebih baik daripada tombol yang
 *  berujung 400.
 *  -> { ditahan, alasan } */
export function syaratTagihanRetur(sale) {
  const tagihan = sale?._retur_tagihan ?? sale?._tukar_unit_tagihan;
  if (tagihan && tagihan.ditahan) {
    return { ditahan: true, alasan: tagihan.alasan || "Tagihan nota bertempo ini belum lunas." };
  }
  if (!tagihan && String(sale?.tempo_pembayaran || "").trim()) {
    return { ditahan: true,
      alasan: "Status tagihan nota bertempo ini belum dimuat — muat ulang halaman, lalu buka lagi." };
  }
  return { ditahan: false, alasan: "" };
}

/** Boleh-tidaknya nota ini diretur TUKAR UNIT (brief owner 22 Sep 2026, I1):
 *  kredit tukar unit hanya bisa dipakai pelanggan dengan nama DAN No HP yang
 *  sama, jadi nota tanpa nama atau tanpa No HP yang sah tidak boleh melahirkan
 *  kredit — tidak akan bisa dipakai siapa pun. Cermin penjaga
 *  `buat_retur_penjualan`; server tetap validator akhir.
 *
 *  Keputusan owner 25 Sep 2026 (29a): nota BERTEMPO yang tagihannya belum lunas
 *  juga tidak boleh ditukar unit. Status tagihan hanya diketahui SERVER (dokumen
 *  piutang Finance) — dibaca dari `sale._tukar_unit_tagihan` (detail nota &
 *  cari-nota, `retur.tagihan_tukar_unit`). Nota bertempo TANPA medan itu =
 *  status tidak diketahui -> ditahan: pilihan yang mati dengan alasan tertulis
 *  lebih baik daripada tombol yang berujung 400.
 *  -> { boleh, nama, noHp, perluHp, alasan } */
export function syaratTukarUnit(sale) {
  const nama = (sale?.nama_pelanggan || "").trim();
  const noHp = sale?.no_hp == null ? "" : String(sale.no_hp);
  if (!nama) {
    return { boleh: false, nama, noHp, perluHp: false,
      alasan: "Nota ini tidak punya nama pelanggan — kredit tukar unit tidak bisa ditautkan ke siapa pun." };
  }
  const tagihan = syaratTagihanRetur(sale);
  if (tagihan.ditahan) {
    return { boleh: false, nama, noHp, perluHp: false, alasan: tagihan.alasan };
  }
  const { kunci, alasan } = periksaKunciHp(noHp);
  if (!kunci) {
    return { boleh: false, nama, noHp, perluHp: true,
      alasan: `Nota ini tidak punya No HP pelanggan yang sah (${pesanHpTidakSah(alasan)}). `
        + "Kredit tukar unit hanya bisa dipakai pelanggan dengan nama DAN No HP yang sama." };
  }
  return { boleh: true, nama, noHp, perluHp: false, alasan: "" };
}

/** Nominal SARAN pengembalian: bruto baris terpilih x (faktor neto + faktor
 *  PPN), dipagari ke sisa hak retur nota. HANYA memengaruhi saran — yang
 *  dikirim ke backend tetap index_item/qty/alasan per baris; server yang
 *  menghitung ulang dan menjadi validator akhir.
 *
 *  Faktor neto PER BARIS (`r.faktorNeto`, N2) dipakai bila ada: pada nota
 *  berdiskon per barang, potongan milik barang itu sendiri — meratakan diskon
 *  ke seluruh nota menyarankan terlalu besar untuk barang berdiskon dan
 *  terlalu kecil untuk yang tidak. */
export function hitungNominalReturDefault(items, faktorNeto, faktorPpn, sisaNilaiMaks) {
  const dipilih = items.filter(r => r.checked);
  const brutoBaris = (r) => (r.harga / (r.qtyAsal || 1)) * (r.qtyRetur || 0);
  let neto;
  if (dipilih.some(r => r.faktorNeto != null)) {
    neto = Math.round(dipilih.reduce(
      (a, r) => a + brutoBaris(r) * ((r.faktorNeto ?? faktorNeto ?? 1) + (faktorPpn ?? 0)), 0));
  } else {
    // Nota tanpa diskon per barang: rumus lama PERSIS (urutan pembulatan sama).
    const bruto = dipilih.reduce((a, r) => a + brutoBaris(r), 0);
    neto = Math.round(bruto * ((faktorNeto ?? 1) + (faktorPpn ?? 0)));
  }
  return Math.max(0, Math.min(neto, sisaNilaiMaks ?? neto));
}

/** Susun baris retur + faktor prorata dari satu nota dan daftar retur atas
 *  nota itu (`GET /retur/penjualan?sale_id`). Retur yang masih aktif
 *  (menunggu approval ATAU sudah disetujui) mengunci qty yang sudah diretur —
 *  dipakai untuk membatasi qty maksimum tiap baris sebelum server menolak
 *  (UX lebih ramah; server tetap validator akhir). */
export function susunReturDariNota(sale, returNota) {
  const aktif = (returNota || []).filter(e => e.status === "menunggu_approval" || e.status === "disetujui");
  const sudahPerIndex = {};
  aktif.forEach(e => (e.items || []).forEach(it => {
    if (it.index_item != null) sudahPerIndex[it.index_item] = (sudahPerIndex[it.index_item] || 0) + (it.qty || 0);
  }));
  // N2: nota berdiskon per barang -> faktor neto PER BARIS (harga bersih baris
  // / harga baris). Nota lain: tidak ada faktor baris, rumus global lama.
  const perBaris = (sale.items || []).some(it => diskonBaris(it) > 0);
  const rows = (sale.items || []).map((it, i) => {
    const qtyAsal = it.qty || 1;
    const sudah = sudahPerIndex[i] || 0;
    const sisa = qtyAsal - sudah;
    const harga = it.harga || 0;
    return {
      index: i, nama_barang: it.nama_barang, harga,
      faktorNeto: perBaris ? (harga > 0 ? Math.max(0, (harga - diskonBaris(it)) / harga) : 1) : undefined,
      diskonBaris: diskonBaris(it),
      qtyAsal, sudah, sisa, checked: false, qtyRetur: sisa > 0 ? 1 : 0, alasan: "",
      // Isi paket dibawa supaya baris paket bisa memberi tahu staf APA yang
      // ada di dalamnya SEBELUM ia mencentang. Tanpa ini staf hanya melihat
      // satu baris bernama "Paket Hemat" — satu-satunya baris yang berharga —
      // lalu mengira mencentangnya sudah mengembalikan semuanya. Padahal
      // yang dulu keluar dari gudang adalah ISINYA: meretur baris paket saja
      // mengembalikan UANGNYA tapi barangnya tetap tercatat terjual, unit
      // ber-IMEI tidak bisa dijual lagi, dan persediaan kurang saji permanen.
      // Lihat §41 memory/KEPUTUSAN-TERTUNDA.md.
      komponen: it.komponen || null,
      isBundleComponent: !!it.is_bundle_component,
    };
  });
  const totalHarga = sale.total_harga || 0;
  // Kunci yang dibaca posting & plafon retur backend (`diskon_terposting`).
  const diskonNominal = diskonTerposting(sale);
  const kenaPpn = !!sale.kena_ppn;
  const ppnNominalSale = kenaPpn ? (sale.ppn_nominal || 0) : 0;
  // Guard total_harga 0 → faktor 1 (tidak ada dasar untuk prorata, jangan
  // sampai divide-by-zero menghasilkan NaN).
  const faktorNeto = totalHarga > 0 ? Math.max(0, (totalHarga - diskonNominal) / totalHarga) : 1;
  const faktorPpn = totalHarga > 0 ? ppnNominalSale / totalHarga : 0;
  const sudahNominalAktif = aktif.reduce((a, e) => a + (e.nominal_kembali || 0), 0);
  const sisaNilaiMaks = Math.max(0, (totalHarga - diskonNominal + ppnNominalSale) - sudahNominalAktif);
  return { rows, faktorNeto, faktorPpn, sisaNilaiMaks, diskonNominal, kenaPpn, ppnNominalSale,
           diskonPerBaris: perBaris };
}
